/**
 * Utility functions for Kayak meta redirect flow
 * Maps fuid values to Kayak API provider codes and metadata
 */

import { DEFAULT_ALLOCATION } from '../constants'
import { Allocation, SearchPlaceType } from '../types'

/**
 * Place types that should not use IATA codes as entity keys
 * (Kayak interprets IATA codes as airports, causing incorrect redirects for cities)
 */
const NON_AIRPORT_PLACE_TYPES: readonly SearchPlaceType[] = [
	SearchPlaceType.CITY,
	SearchPlaceType.REGION,
	SearchPlaceType.TOWN,
	SearchPlaceType.COUNTRY,
	SearchPlaceType.LANDMARK,
	SearchPlaceType.HOTEL,
	SearchPlaceType.AIRPORT,
]

/**
 * destination-key values for which we strip destination-id when redirecting to Kayak.
 * Airport: we send IATA (e.g. CDG) so backend can build slug with -lCDG; when we stripped, backend built "...(CDG),-" → 404.
 */
const STRIP_DEST_ID_DESTINATION_KEYS = new Set<string>([
	// 'airport', 'ap' — keep destination-id (IATA) so backend can build valid airport slug
	// 'city', 'region', 'town', ... — uncomment to try strip for city/region
])

/**
 * Checks if a string is an IATA airport code (3 uppercase letters)
 */
export function isIataCode(value: string | undefined): boolean {
	return !!value && /^[A-Z]{3}$/.test(value)
}

/**
 * Checks if a place type should not use IATA codes as entity keys (strip destination-id for Kayak).
 * Accepts both SearchPlaceType enum values and destination-key strings from the form.
 */
export function isNonAirportPlaceType(placeType: SearchPlaceType | string): boolean {
	const s = typeof placeType === 'string' ? placeType : ''
	return (
		NON_AIRPORT_PLACE_TYPES.includes(placeType as SearchPlaceType) ||
		STRIP_DEST_ID_DESTINATION_KEYS.has(s.toLowerCase())
	)
}

export enum RedirectRoute {
	REDIRECT = 'redirect',
	PASSTHRU = 'passthru',
}

export enum KayakProvider {
	BOOKING = 'bok',
	AGODA = 'aga',
	EXPEDIA = 'exp',
	HOTELS = 'hot',
}

interface PartnerConfig {
	ap: KayakProvider
	displayName: string
	logoName: string
}

const PARTNERS: Record<string, PartnerConfig> = {
	// Numeric partner IDs
	'9005': { ap: KayakProvider.BOOKING, displayName: 'Booking.com', logoName: 'booking' },
	'9003': { ap: KayakProvider.AGODA, displayName: 'Agoda.com', logoName: 'agoda' },
	'9001': { ap: KayakProvider.EXPEDIA, displayName: 'Expedia', logoName: 'expedia' },
	'9002': { ap: KayakProvider.HOTELS, displayName: 'Hotels.com', logoName: 'hotels' },
	// Short codes (ap values) directly in fuid
	[KayakProvider.BOOKING]: { ap: KayakProvider.BOOKING, displayName: 'Booking.com', logoName: 'booking' },
	[KayakProvider.AGODA]: { ap: KayakProvider.AGODA, displayName: 'Agoda.com', logoName: 'agoda' },
	[KayakProvider.EXPEDIA]: { ap: KayakProvider.EXPEDIA, displayName: 'Expedia', logoName: 'expedia' },
	[KayakProvider.HOTELS]: { ap: KayakProvider.HOTELS, displayName: 'Hotels.com', logoName: 'hotels' },
}

function getPartnerConfig(fuid: string | null | undefined): PartnerConfig | null {
	if (!fuid || typeof fuid !== 'string') {
		return null
	}

	const normalized = fuid.toLowerCase().trim()
	return PARTNERS[normalized] || null
}

/**
 * Type guard to check if fuid is valid
 * @param fuid - Partner ID to validate
 * @returns true if fuid is valid and maps to a known partner
 */
export function isValidFuid(fuid: string | null | undefined): fuid is string {
	return !!fuid && !!getPartnerConfig(fuid)
}

export function mapFuidToKayakProvider(
	fuid: string | null | undefined
): KayakProvider | null {
	return getPartnerConfig(fuid)?.ap ?? null
}

const kayakLangMap: Record<string, string> = {
	he: 'iw',
	cz: 'cs',
	gr: 'el',
	jp: 'ja',
}

/**
 * Maps application locale to Kayak-compatible language code
 * @param lang - App language code (e.g., 'he', 'en')
 * @returns Kayak-compatible language code (e.g., 'iw', 'en')
 */
export function getKayakLanguageCode(lang: string): string {
	return kayakLangMap[lang] || lang
}

/**
 * Checks if the current flow is a Kayak meta redirect flow
 * @param f - The 'f' parameter from URL (e.g., 'km', 'k', 'b')
 * @param fuid - Optional fuid parameter
 * @returns true if this is a Kayak meta flow (f=km)
 */
export function isKayakMetaFlow(
	f: string | null | undefined,
	fuid?: string | null | undefined
): boolean {
	return f === 'km' && isValidFuid(fuid)
}

/**
 * Gets the display name for a partner based on fuid
 * @param fuid - Partner ID from URL
 * @returns Display name or null if not found
 */
export function getPartnerDisplayName(fuid: string | null | undefined): string | null {
	return getPartnerConfig(fuid)?.displayName ?? null
}

/**
 * Gets the partner name (for logo path) based on fuid
 * @param fuid - Partner ID from URL
 * @returns Partner name for logo path (e.g., 'booking', 'agoda') or null if not found
 */
export function getPartnerName(fuid: string | null | undefined): string | null {
	return getPartnerConfig(fuid)?.logoName ?? null
}

/**
 * Gets the partner logo path based on fuid
 * @param fuid - Partner ID from URL
 * @returns Logo path (e.g., '/images/booking.png') or null if not found
 */
export function getPartnerLogoPath(fuid: string | null | undefined): string | null {
	const partnerName = getPartnerName(fuid)
	return partnerName ? `/images/${partnerName}.png` : null
}

export function isKayakRedirectAllowedPath(pathname: string): boolean {
	// Safety: never allow redirect logic on redirect/passthru routes themselves
	if (pathname.includes('/redirect') || pathname.includes('/passthru')) {
		return false
	}

	// Allow only:
	// 1) Main page: /{lang}
	// 2) Hotel main page: /{lang}/mp/hotel/...
	// This regex intentionally allows any locale segment and any remaining path.
	return /^\/[^/]+(?:\/)?$/.test(pathname) || /^\/[^/]+\/mp\/hotel(\/.*)?$/.test(pathname)
}

/**
 * Builds a redirect or passthru URL
 * @param lang - Language code (e.g., 'en', 'fr')
 * @param isKayakMetaFlow - Whether this is a Kayak meta redirect flow (determines redirect vs passthru)
 * @param params - URL search parameters as URLSearchParams or Record with string/number/boolean values (null/undefined are filtered out)
 * @returns Formatted URL string (e.g., '/en/passthru?uid=123&f=b')
 */
export function buildRedirectUrl(
	lang: string,
	isKayakMetaFlow: boolean,
	params: URLSearchParams | Record<string, unknown>
): string {
	const route = isKayakMetaFlow ? RedirectRoute.REDIRECT : RedirectRoute.PASSTHRU
	const searchParams = params instanceof URLSearchParams
		? params
		: new URLSearchParams(
			Object.entries(params)
				.filter(([_, value]) => value != null && value !== '') // Filter out null, undefined, and empty strings
				.map(([key, value]) => [key, String(value)])
		)
	return `/${lang}/${route}?${searchParams.toString()}`
}

/**
 * Normalizes a date string by extracting just the date part (removes time/timezone)
 * @param value - Date string that may include time/timezone (e.g., "2026-01-20T14:30:26+03:00")
 * @returns Date string in YYYY-MM-DD format or undefined if invalid
 */
export function normalizeDate(value: string | undefined): string | undefined {
	if (!value) return undefined
	return value.includes('T') ? value.split('T')[0] : value
}

/**
 * Derives a Kayak entityKey from a place ID
 * Maps internal formats (C:xxx, H:xxx) to Kayak API formats (khotel:xxx, kplace:xxx)
 * @param placeID - Place ID in various formats (C:Z36014, H:185692, khotel:123, etc.)
 * @returns Kayak entityKey string or undefined if should be omitted
 */
export function deriveEntityKey(placeID: string | undefined): string | undefined {
	if (!placeID) return undefined

	// - If already has a valid Kayak prefix (city, hotel, khotel, h, kplace, latlon, place, polygon), use as-is
	if (/^(city|hotel|khotel|h|kplace|latlon|place|polygon):/.test(placeID)) {
		return placeID
	}

	// - Map our internal formats to Kayak entityKey formats:
	//   C:xxx → city:xxx (Use city: prefix for better matching)
	//   H:xxx → khotel:xxx (H means hotel, extract numeric ID and use khotel: format)
	//   khotel:xxx → keep as-is
	const prefixMatch = placeID.match(/^([CH]|khotel):(.*)$/)
	if (prefixMatch) {
		const prefix = prefixMatch[1]
		let idPart = prefixMatch[2]

		if (prefix === 'C') {
			// Strip leading letter prefixes from city ID (e.g., Z36014 → 36014)
			const numericMatch = idPart.match(/^[A-Z]?(\d+)$/)
			if (numericMatch) {
				idPart = numericMatch[1]
			}
			return `city:${idPart}`
		} else if (prefix === 'H' || prefix === 'khotel') {
			// Strip leading letter prefixes from hotel ID (e.g., Z36014 → 36014)
			const numericMatch = idPart.match(/^[A-Z]?(\d+)$/)
			if (numericMatch) {
				idPart = numericMatch[1] // Extract just the numeric part
			} else if (!/^\d+$/.test(idPart)) {
				// If ID doesn't contain numeric part, return undefined
				return undefined
			}
			// Use khotel: format to match working getHotelsData implementation (line 233)
			return `khotel:${idPart}`
		}
	}

	// - For IATA codes (3 uppercase letters), use as-is (no prefix needed)
	if (/^[A-Z]{3}$/.test(placeID)) {
		return placeID
	}

	// - For purely numeric IDs, assume they're place IDs and use kplace: prefix
	if (/^\d+$/.test(placeID)) {
		return `kplace:${placeID}`
	}

	// - Default: return undefined for unknown formats (let API match by name/type)
	return undefined
}

/**
 * Maps location type string to SearchPlaceType enum
 * @param loctype - Location type string (e.g., 'hotel', 'city', 'airport')
 * @returns SearchPlaceType enum value, defaults to CITY
 */
export function mapPlaceType(loctype: string | undefined): SearchPlaceType {
	const type = loctype?.toLowerCase()
	switch (type) {
		case 'hotel':
			return SearchPlaceType.HOTEL
		case 'city':
			return SearchPlaceType.CITY
		case 'town':
			return SearchPlaceType.TOWN
		case 'region':
			return SearchPlaceType.REGION
		case 'touristregion':
			return SearchPlaceType.REGION
		case 'airport':
			return SearchPlaceType.AIRPORT
		case 'country':
			return SearchPlaceType.COUNTRY
		case 'landmark':
			return SearchPlaceType.LANDMARK
		case 'lm':
			return SearchPlaceType.LANDMARK
		case 'hood':
			return SearchPlaceType.CITY
		case 'borough':
			return SearchPlaceType.CITY
		default:
			return SearchPlaceType.CITY
	}
}

/**
 * Parses room allocations from adults and children parameters
 * Supports pipe-separated values for multiple rooms (e.g., "2|3" for 2 rooms with 2 and 3 adults)
 * @param adultsParam - Adults count(s) as string or number, can be pipe-separated (e.g., "2|3")
 * @param childrenParam - Children count(s) as string or number, can be pipe-separated (e.g., "1|0")
 * @returns Array of Allocation objects, defaults to [DEFAULT_ALLOCATION] if no adults param
 */
export function parseAllocations(
	adultsParam: string | number | undefined,
	childrenParam: string | number | undefined
): Allocation[] {
	if (!adultsParam && adultsParam !== 0) return [DEFAULT_ALLOCATION]

	const adultsCountList = String(adultsParam).split('|')
	const childrenCountList = childrenParam ? String(childrenParam).split('|') : []

	return adultsCountList.map((adultCount, index) => {
		const childCount = parseInt(childrenCountList[index] || '0') || 0
		const childAges: number[] = childCount > 0 ? new Array(childCount).fill(8) : []

		const parsedAdults = parseInt(adultCount)
		return {
			adults: parsedAdults > 0 ? parsedAdults : 2,
			childAge: childAges,
		}
	})
}

const COUNTRY_LANGUAGE_MAP: Record<string, string> = {
	ar: 'SA',
	he: 'IL',
}

/**
 * Maps a language code to the corresponding country code for Kayak API requests
 * @param lang - Language code (e.g., 'en', 'ar', 'he')
 * @returns Country code (e.g., 'EN', 'SA', 'IL')
 */
export function getCountryCode(lang: string): string {
	return COUNTRY_LANGUAGE_MAP[lang] || lang.toUpperCase()
}

/**
 * Injects a tracking ID into the URL's p= parameter
 * Handles both cases: p= in middle of URL (p=&) and p= at end of URL (p=$)
 * Only matches standalone 'p=' parameter (preceded by ? or &), not 'pp=' or 'ap='
 * @param url - The URL to inject tracking ID into
 * @param trackingID - The tracking ID to inject
 * @returns URL with tracking ID injected, or original URL if no p= parameter found
 */
export function injectTrackingIdIntoUrl(url: string, trackingID: string): string {
	if (!url || !trackingID) return url
	// Match p= only when preceded by ? or & (standalone parameter)
	return url.replace(/([?&])p=(&|$)/, `$1p=${trackingID}$2`)
}

/**
 * Adds hf=3 parameter to Booking.com URLs to filter for apartments only
 * @param url - The URL to add the parameter to
 * @param f - Front partner code (e.g., 'b', 'k')
 * @param b - Back partner code (e.g., 'b')
 * @returns URL with hf=3 parameter added if it's a Booking.com redirect, otherwise original URL
 */
export function addBookingFilterParam(url: string, f?: string | null, b?: string | null): string {
	if (!url) return url

	// Check if this is a Booking.com redirect (f=b or b=b)
	const isBookingRedirect = f === 'b' || b === 'b'
	if (!isBookingRedirect) return url

	// Add hf=3 parameter to force apartments display on Booking.com
	const separator = url.includes('?') ? '&' : '?'
	return `${url}${separator}hf=3`
}
